/**
 * Header — TASKS 4.1, 6.2, 6.3ב · DESIGN.md 5.1.
 *
 * ⭐ Sticky, גובה קבוע (100px מובייל / 124px דסקטופ — הוגדל שוב 21.08.2026,
 *    ראו ההערה בגוף הקומפוננטה), רקע גרדיאנט קרם עדין
 *    (`.ornate-surface`, פידבק לקוח 20.08.2026 לילה) עם מלמלה מקושטת
 *    (`.ornate-scallop-edge`) במקום קו `border-b` פשוט — ראו `index.css`.
 *    `shadow-card` נכנס אחרי גלילה (200ms) — דרך `useScroll` של
 *    `motion` (⛔ לא `window.addEventListener('scroll')`, אסור לפי DESIGN.md 6.3).
 *
 * ⭐ תפריט מובייל: מלכודת פוקוס + `Esc` + החזרת פוקוס לכפתור ההמבורגר —
 *    אותו דפוס בדיוק כמו `Lightbox.tsx` ו-`AccessibilityWidget.tsx` (DESIGN.md
 *    5.1: "אותו דפוס כמו Lightbox ו-AccessibilityWidget הקיימים").
 *
 * ⚠️ **סטייה מתועדת אחת:** DESIGN.md 5.1 מבקש CTA "מוקטן (גובה 40px)" בכותרת.
 *    `.btn` אוכף `min-height: 44px` כתנאי קבלה של WCAG 2.5.5 (יעד מגע, כבר
 *    אומת ב-axe בכל האתר) — 40px היה שובר את זה. ה-CTA כאן משתמש ב-`.btn-cta`
 *    הרגיל (44px), רק עם `text-sm` לצמצום ויזואלי בלי לגעת בגובה יעד המגע.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'motion/react';
import { ShoppingCart } from '@phosphor-icons/react';
import { COMPANY_NAME, LOGO_HEIGHT, LOGO_WIDTH } from '../content/businessInfo.js';
import { NAV_LINKS } from '../content/navLinks.js';
import { useCart } from '../cart/CartContext.js';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

const overlayVariants = { hidden: { opacity: 0 }, visible: { opacity: 1 } };
const listVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05 } },
};
const itemVariants = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } };

const hamburgerTop = { closed: { rotate: 0, y: 0 }, open: { rotate: 45, y: 7 } };
const hamburgerMiddle = { closed: { opacity: 1 }, open: { opacity: 0 } };
const hamburgerBottom = { closed: { rotate: 0, y: 0 }, open: { rotate: -45, y: -7 } };

function NavItem({ to, label, end }: { to: string; label: string; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className="text-link nav-link-hover relative inline-block py-2 font-medium"
    >
      {({ isActive }) => (
        <>
          {label}
          {isActive && (
            <motion.span
              aria-hidden="true"
              layoutId="header-active-underline"
              className="absolute inset-x-0 -bottom-0.5 h-0.5"
              style={{ backgroundColor: 'var(--color-copper)' }}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.15 }}
            />
          )}
        </>
      )}
    </NavLink>
  );
}

export function Header() {
  const cart = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, 'change', (latest) => {
    setScrolled(latest > 8);
  });

  // ⭐ TASKS 6.2 · DESIGN.md 6.2 — spring "קפיצה" על ה-badge בכל הוספה לעגלה.
  // ⚠️ ⛔ קופץ רק כשהמונה **עולה** (הוספה), ⛔ לא כשהוא יורד (הסרה) או
  //    בטעינה הראשונה של הדף — `prevCount` נשמר ב-ref כדי לא לגרום לתלות
  //    מעגלית ב-effect.
  const prevCountRef = useRef(cart.items.length);
  const [pulseKey, setPulseKey] = useState(0);
  useEffect(() => {
    if (cart.items.length > prevCountRef.current) setPulseKey((k) => k + 1);
    prevCountRef.current = cart.items.length;
  }, [cart.items.length]);

  function closeMenu() {
    setMobileOpen(false);
    triggerRef.current?.focus();
  }

  useEffect(() => {
    if (mobileOpen) dialogRef.current?.focus();
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
        return;
      }
      if (event.key === 'Tab') {
        const dialog = dialogRef.current;
        if (!dialog) return;
        const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
        if (focusable.length === 0) return;
        const first = focusable[0] as HTMLElement;
        const last = focusable[focusable.length - 1] as HTMLElement;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen]);

  return (
    <motion.header
      className="ornate-surface sticky top-0 z-40 transition-[box-shadow] duration-200"
      style={{ boxShadow: scrolled ? 'var(--shadow-card)' : 'none' }}
    >
      {/* ⭐ Feedback1.md סעיף 3 (19.08.2026) — טקסט הניווט וגובה הלוגו הוגדלו
          משמעותית (בערך פי 2 לטקסט) לבקשת טל. גובה המיכל גדל בהתאם כדי
          שהלוגו הגדול יותר לא ייחתך, ונשאר `items-center` כדי שהלוגו יישאר
          מיושר אנכית למרכז מול שורת הניווט הגבוהה יותר.
          ⭐ עודכן שוב 20.08.2026 (פידבק אריאנה, בצפייה חיה) — `New Logo.jpeg`
          מכיל שורת תגית + טלפון בתוך התמונה עצמה (CLAUDE.md סעיף 11); בגובה
          הקודם (56-64px) הטקסט הפנימי הזה לא היה קריא בפועל. גובה המיכל גדל
          בהתאם כך שהלוגו הגדול עדיין לא נחתך.
          ⭐ עודכן שוב 21.08.2026 (פידבק אריאנה) — עוד הגדלה, בגבול המרווח
          שנשאר במיכל (92px→100px מובייל, 116px→124px דסקטופ).
          🔴 **עודכן שוב 27.08.2026 — TASKS 6.7, באג זום 400% אמיתי שאריאנה
          תפסה:** הערכים הקבועים כאן הוצאו למשתני CSS גלובליים (`--header-h`/
          `--logo-h` ב-`index.css`), עם שכבה שלישית ל-viewport נמוך
          (`max-height: 500px`) שמקטינה את שניהם — בלי זה ה-Header הקבוע-
          בגודלו תפס 60% מגובה המסך בזום גבוה. ⚠️ **כל שינוי עתידי לגובה
          ההדר נכנס ב-`index.css` בלבד** — `top-[var(--header-h)]` בתפריט
          הנייד למטה ו-`lg:top-[calc(var(--header-h)+12px)]` ב-`CartPage.tsx`
          מסתנכרנים אוטומטית, ⛔ בלי לגעת בהם שוב. */}
      <div className="mx-auto flex h-[var(--header-h)] max-w-6xl items-center justify-between gap-4 px-6">
        <Link to="/" className="shrink-0">
          <img
            src="/brand/logo.png"
            alt={`${COMPANY_NAME} - לדף הבית`}
            width={LOGO_WIDTH}
            height={LOGO_HEIGHT}
            className="h-[var(--logo-h)] w-auto"
          />
        </Link>

        <nav aria-label="ניווט ראשי" className="hidden md:block">
          <ul className="flex items-center gap-x-8 text-2xl">
            {NAV_LINKS.map((link) => (
              <li key={link.to}>
                <NavItem {...link} />
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex items-center gap-3">
          <NavLink
            to="/cart"
            aria-label="עגלת בקשות"
            className="text-link relative inline-flex h-11 w-11 items-center justify-center"
          >
            <ShoppingCart size={24} weight="regular" aria-hidden="true" />
            {cart.items.length > 0 && (
              <motion.span
                key={pulseKey}
                initial={{ scale: 1, opacity: 0 }}
                animate={{ scale: [1, 1.15, 1], opacity: 1 }}
                transition={{ type: 'spring', visualDuration: 0.3, bounce: 0.4 }}
                className="bg-link text-canvas tabular absolute -top-1 -end-1 inline-flex min-w-5 items-center justify-center rounded-full px-1 py-0.5 text-xs font-bold"
                aria-hidden="true"
              >
                {cart.items.length}
              </motion.span>
            )}
          </NavLink>

          <Link to="/cart" className="btn btn-cta hidden text-sm md:inline-flex">
            בקשת הצעת מחיר
          </Link>

          <button
            ref={triggerRef}
            type="button"
            className="flex h-11 w-11 items-center justify-center md:hidden"
            aria-label={mobileOpen ? 'סגירת התפריט' : 'פתיחת התפריט'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav-panel"
            aria-haspopup="dialog"
            onClick={() => setMobileOpen((open) => !open)}
          >
            <span aria-hidden="true" className="flex h-4 w-6 flex-col justify-between">
              <motion.span
                className="bg-ink block h-0.5 w-6"
                variants={hamburgerTop}
                animate={mobileOpen ? 'open' : 'closed'}
                transition={{ duration: 0.2 }}
              />
              <motion.span
                className="bg-ink block h-0.5 w-6"
                variants={hamburgerMiddle}
                animate={mobileOpen ? 'open' : 'closed'}
                transition={{ duration: 0.2 }}
              />
              <motion.span
                className="bg-ink block h-0.5 w-6"
                variants={hamburgerBottom}
                animate={mobileOpen ? 'open' : 'closed'}
                transition={{ duration: 0.2 }}
              />
            </span>
          </button>
        </div>
      </div>

      <div className="ornate-scallop-edge" aria-hidden="true" />

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            id="mobile-nav-panel"
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            className="bg-canvas fixed inset-x-0 bottom-0 top-[var(--header-h)] z-40 overflow-y-auto md:hidden"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={overlayVariants}
            transition={{ duration: 0.2 }}
          >
            <h2 id={titleId} className="sr-only">
              תפריט ניווט
            </h2>
            <nav aria-label="ניווט ראשי" className="px-6 py-6">
              <motion.ul
                variants={listVariants}
                initial="hidden"
                animate="visible"
                className="flex flex-col gap-1"
              >
                {NAV_LINKS.map((link) => (
                  <motion.li
                    key={link.to}
                    variants={itemVariants}
                    transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <NavLink
                      to={link.to}
                      end={link.end}
                      onClick={closeMenu}
                      className={({ isActive }) =>
                        `block min-h-11 py-3 text-lg font-medium ${
                          isActive ? 'text-ink' : 'text-link'
                        }`
                      }
                    >
                      {link.label}
                    </NavLink>
                  </motion.li>
                ))}
                <motion.li
                  variants={itemVariants}
                  transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                  className="mt-3"
                >
                  <Link to="/cart" onClick={closeMenu} className="btn btn-cta w-full">
                    בקשת הצעת מחיר
                  </Link>
                </motion.li>
              </motion.ul>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
